import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronDown, Menu, User, Settings, LogOut } from 'lucide-react'
import { useRecoilState } from 'recoil'
import { authState } from '../../atoms/auth'
import { logout } from '../../services/apiService'
import { useAuthState } from '../../utils/authUtils'
import { Button } from '../ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet'
import { cn } from '../../lib/utils'
import { getHeaderNavigation, type HeaderMenuItem } from '../../config/navigation'

const Header = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [auth] = useRecoilState(authState)
  const { initUser } = useAuthState()
  const [mobileOpen, setMobileOpen] = useState(false)
  const apiBaseUrl =
    process.env.REACT_APP_API_BASE_URL ||
    (window.location.hostname === 'localhost'
      ? 'http://localhost:9000'
      : window.location.origin)

  const visibleMenuItems = getHeaderNavigation()

  const moveToVirtualExchange = () => {
    window.location.href = `${apiBaseUrl.replace(
      /\/+$/,
      ''
    )}/api/main/virtual-exchange/sso`
  }

  const handleMoveMenu = (item: HeaderMenuItem) => {
    if (item.path === '/virtual-exchange-sso') {
      moveToVirtualExchange()
      return
    }
    navigate(item.path)
  }

  const isMenuActive = (item: HeaderMenuItem): boolean => {
    const matchesPath = (path: string) =>
      path.endsWith('/') ? location.pathname.startsWith(path) : location.pathname === path

    if (matchesPath(item.path)) return true
    if (item.activePaths?.some(matchesPath)) return true

    return !!item.children?.some((child) => isMenuActive(child))
  }

  const handleClickLogout = async () => {
    const { data: res } = await logout()
    if (res.statusCode === 200) {
      initUser()
      navigate('/')
    }
  }

  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 w-full max-w-[1200px] items-center justify-between px-4 md:px-6">
        <div className="flex items-center gap-2">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent>
              <div className="mb-8 flex items-center justify-between">
                <button onClick={() => navigate('/')} className="text-left">
                  <img src="/images/logo/2.png" alt="logo" className="h-[50px] w-auto" />
                </button>
              </div>
              <nav className="flex flex-col gap-1">
                {visibleMenuItems.map((item) => (
                  <div key={item.path}>
                    <button
                      onClick={() => {
                        handleMoveMenu(item)
                        setMobileOpen(false)
                      }}
                      className="w-full rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                    >
                      {item.label}
                    </button>
                    {item.children?.length ? (
                      <div className="ml-3 border-l border-slate-200 pl-2">
                        {item.children.map((child) => (
                          <button
                            key={child.path}
                            onClick={() => {
                              handleMoveMenu(child)
                              setMobileOpen(false)
                            }}
                            className="w-full rounded-xl px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-100"
                          >
                            {child.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </nav>
            </SheetContent>
          </Sheet>

          <button onClick={() => navigate('/')} className="flex items-center">
            <img src="/images/logo/2.png" alt="Oreun Academy" className="h-[50px] w-auto" />
          </button>
        </div>

        <nav className="hidden items-center gap-2 md:flex">
          {visibleMenuItems.map((item) =>
            item.children?.length ? (
              <div key={item.path} className="group relative">
                <button
                  onClick={() => handleMoveMenu(item)}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900',
                    isMenuActive(item) && 'bg-slate-100 text-slate-900'
                  )}
                >
                  {item.label}
                  <ChevronDown className="h-4 w-4" />
                </button>
                <div className="invisible absolute left-0 top-full z-50 min-w-[220px] rounded-xl border border-slate-200 bg-white p-1 opacity-0 shadow-toss-lg transition group-hover:visible group-hover:opacity-100">
                  {item.children.map((child) => (
                    <button
                      key={child.path}
                      onClick={() => handleMoveMenu(child)}
                      className={cn(
                        'w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-100',
                        isMenuActive(child) && 'bg-slate-100 text-slate-900'
                      )}
                    >
                      {child.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <button
                key={item.path}
                onClick={() => handleMoveMenu(item)}
                className={cn(
                  'rounded-full px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900',
                  isMenuActive(item) && 'bg-slate-100 text-slate-900'
                )}
              >
                {item.label}
              </button>
            )
          )}
        </nav>

        {auth.id ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="rounded-full focus:outline-none">
                <Avatar className="h-9 w-9 border border-slate-200">
                  <AvatarImage src={auth.profileImage || ''} alt={auth.username || 'user'} />
                  <AvatarFallback>{(auth.username || 'U').slice(0, 1)}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => navigate('/mypage')}>
                <User className="mr-2 h-4 w-4" />
                마이페이지
              </DropdownMenuItem>
              {auth.isAdmin === 1 && (
                <DropdownMenuItem onClick={() => navigate('/admin/user')}>
                  <Settings className="mr-2 h-4 w-4" />
                  관리자
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleClickLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                로그아웃
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button size="sm" onClick={() => navigate('/login')}>
            로그인
          </Button>
        )}
      </div>
    </header>
  )
}

export default Header
